import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import api from '../api';

interface AddCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddCustomerDialog({ open, onClose, onSuccess }: AddCustomerDialogProps) {
  const [formData, setFormData] = useState({
    companyName: '',
    tenantId: '',
    mspTenantId: '',
    primaryContactEmail: '',
    primaryContactName: '',
    phoneNumber: '',
    subscriptionTier: 'Standard',
    monthlyFee: 99.00,
    initialConfigFee: 2500.00,
    initialConfigLicenseType: 'M365 Business Premium',
    notes: '',
    isMSP: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantIdHelper, setTenantIdHelper] = useState('');

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Adjust defaults based on MSP status
    if (field === 'isMSP' && value === true) {
      setFormData(prev => ({
        ...prev,
        subscriptionTier: 'MSP',
        monthlyFee: 0.00,
        initialConfigFee: 0.00,
        initialConfigLicenseType: 'MSP Partner'
      }));
    } else if (field === 'isMSP' && value === false) {
      setFormData(prev => ({
        ...prev,
        subscriptionTier: 'Standard',
        monthlyFee: 99.00,
        initialConfigFee: 2500.00,
        initialConfigLicenseType: 'M365 Business Premium'
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.companyName.trim()) {
        setError('Company name is required');
        return;
      }
      if (!formData.primaryContactEmail.trim()) {
        setError('Primary contact email is required');
        return;
      }
      if (!formData.primaryContactName.trim()) {
        setError('Primary contact name is required');
        return;
      }

      if (formData.isMSP) {
        if (!formData.mspTenantId.trim()) {
          setError('MSP Tenant ID is required');
          return;
        }
        // Validate MSP Tenant ID format (should be a GUID)
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!guidRegex.test(formData.mspTenantId.trim())) {
          setError('MSP Tenant ID must be a valid GUID format (e.g., xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)');
          return;
        }
      } else {
        if (!formData.tenantId.trim()) {
          setError('Azure AD Tenant ID is required');
          return;
        }
        // Validate Tenant ID format (should be a GUID)
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!guidRegex.test(formData.tenantId.trim())) {
          setError('Tenant ID must be a valid GUID format (e.g., xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)');
          return;
        }
      }

      // Create customer
      if (formData.isMSP) {
        // Create MSP account
        await api.post('/TCAddMSP', {
          MSPTenantID: formData.mspTenantId,
          companyName: formData.companyName,
          primaryContactEmail: formData.primaryContactEmail,
          primaryContactName: formData.primaryContactName,
          primaryContactPhone: formData.phoneNumber,
          billingModel: 'Aggregated',
          notes: formData.notes
        });
      } else {
        // Create regular customer
        await api.post('/TCAddCustomer', {
          companyName: formData.companyName,
          tenantId: formData.tenantId,
          primaryContactEmail: formData.primaryContactEmail,
          primaryContactName: formData.primaryContactName,
          phoneNumber: formData.phoneNumber,
          subscriptionTier: formData.subscriptionTier,
          monthlyFee: formData.monthlyFee,
          initialConfigFee: formData.initialConfigFee,
          initialConfigLicenseType: formData.initialConfigLicenseType,
          notes: formData.notes
        });
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data || 'Failed to create customer');
      console.error('Error creating customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      companyName: '',
      tenantId: '',
      mspTenantId: '',
      primaryContactEmail: '',
      primaryContactName: '',
      phoneNumber: '',
      subscriptionTier: 'Standard',
      monthlyFee: 99.00,
      initialConfigFee: 2500.00,
      initialConfigLicenseType: 'M365 Business Premium',
      notes: '',
      isMSP: false
    });
    setError(null);
    setTenantIdHelper('');
    onClose();
  };

  const getTenantIdHelperUrl = () => {
    if (formData.primaryContactEmail.includes('@')) {
      const domain = formData.primaryContactEmail.split('@')[1];
      return `https://login.microsoftonline.com/${domain}/.well-known/openid-configuration`;
    }
    return '';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Add New {formData.isMSP ? 'MSP Partner' : 'Customer'}
          <Tooltip title={formData.isMSP ? 'MSP partners can manage multiple customer tenants' : 'Regular customers access only their own tenant'}>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* MSP Toggle */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '60px' }}>
              Regular
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isMSP}
                  onChange={(e) => handleChange('isMSP', e.target.checked)}
                  color="primary"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
            <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '60px' }}>
              MSP
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {formData.isMSP 
              ? 'This account will have access to manage multiple customer tenants via DNS verification'
              : 'This is a regular customer account with access to their own tenant only'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Company Information */}
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Company Information
        </Typography>

        <TextField
          fullWidth
          label="Company Name"
          value={formData.companyName}
          onChange={(e) => handleChange('companyName', e.target.value)}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="Primary Contact Email"
          type="email"
          value={formData.primaryContactEmail}
          onChange={(e) => handleChange('primaryContactEmail', e.target.value)}
          margin="normal"
          required
          helperText="Admin email address for this account"
        />

        <TextField
          fullWidth
          label="Primary Contact Name"
          value={formData.primaryContactName}
          onChange={(e) => handleChange('primaryContactName', e.target.value)}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.target.value)}
          margin="normal"
        />

        {formData.isMSP && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* MSP Azure AD Information */}
            <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              MSP Azure AD Tenant Information
              <Tooltip title="MSP's Microsoft 365 tenant details - required for authentication">
                <HelpOutlineIcon fontSize="small" color="action" />
              </Tooltip>
            </Typography>

            <TextField
              fullWidth
              label="MSP Azure AD Tenant ID"
              value={formData.mspTenantId}
              onChange={(e) => handleChange('mspTenantId', e.target.value)}
              margin="normal"
              required
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              helperText="MSP's tenant ID from Azure Portal → Azure AD → Overview → Tenant ID"
              InputProps={{
                endAdornment: formData.primaryContactEmail && (
                  <InputAdornment position="end">
                    <Tooltip title="Click to get Tenant ID lookup URL">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = getTenantIdHelperUrl();
                          if (url) {
                            window.open(url, '_blank');
                            setTenantIdHelper('Look for "tenant_region_scope" in the JSON response');
                          }
                        }}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />

            {tenantIdHelper && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {tenantIdHelper}
              </Alert>
            )}
          </>
        )}

        {!formData.isMSP && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Azure AD Information */}
            <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Azure AD Tenant Information
              <Tooltip title="Customer's Microsoft 365 tenant details">
                <HelpOutlineIcon fontSize="small" color="action" />
              </Tooltip>
            </Typography>

            <TextField
              fullWidth
              label="Azure AD Tenant ID"
              value={formData.tenantId}
              onChange={(e) => handleChange('tenantId', e.target.value)}
              margin="normal"
              required
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              helperText="Find in Azure Portal → Azure AD → Overview → Tenant ID"
              InputProps={{
                endAdornment: formData.primaryContactEmail && (
                  <InputAdornment position="end">
                    <Tooltip title="Click to get Tenant ID lookup URL">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = getTenantIdHelperUrl();
                          if (url) {
                            window.open(url, '_blank');
                            setTenantIdHelper('Look for "tenant_region_scope" in the JSON response');
                          }
                        }}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />

            {tenantIdHelper && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {tenantIdHelper}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Subscription Information */}
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Subscription Information
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel>Subscription Tier</InputLabel>
              <Select
                value={formData.subscriptionTier}
                label="Subscription Tier"
                onChange={(e) => handleChange('subscriptionTier', e.target.value)}
              >
                <MenuItem value="Basic">Basic</MenuItem>
                <MenuItem value="Standard">Standard</MenuItem>
                <MenuItem value="Premium">Premium</MenuItem>
                <MenuItem value="Enterprise">Enterprise</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Monthly Fee"
              type="number"
              value={formData.monthlyFee}
              onChange={(e) => handleChange('monthlyFee', parseFloat(e.target.value))}
              margin="normal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Recurring monthly subscription fee"
            />

            <TextField
              fullWidth
              label="Initial Configuration Fee"
              type="number"
              value={formData.initialConfigFee}
              onChange={(e) => handleChange('initialConfigFee', parseFloat(e.target.value))}
              margin="normal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="One-time setup fee"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Initial Configuration License Type</InputLabel>
              <Select
                value={formData.initialConfigLicenseType}
                label="Initial Configuration License Type"
                onChange={(e) => handleChange('initialConfigLicenseType', e.target.value)}
              >
                <MenuItem value="M365 Business Basic">M365 Business Basic</MenuItem>
                <MenuItem value="M365 Business Standard">M365 Business Standard</MenuItem>
                <MenuItem value="M365 Business Premium">M365 Business Premium</MenuItem>
                <MenuItem value="M365 E3">M365 E3</MenuItem>
                <MenuItem value="M365 E5">M365 E5</MenuItem>
                <MenuItem value="Intune + Entra P1">Intune + Entra P1</MenuItem>
                <MenuItem value="Intune + Entra P2">Intune + Entra P2</MenuItem>
                <MenuItem value="Exchange Online Only">Exchange Online Only</MenuItem>
                <MenuItem value="Exchange Online + Entra P1">Exchange Online + Entra P1</MenuItem>
                <MenuItem value="Exchange Online + Entra P2">Exchange Online + Entra P2</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes"
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          margin="normal"
          multiline
          rows={3}
          helperText="Optional internal notes about this customer"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : `Create ${formData.isMSP ? 'MSP Partner' : 'Customer'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
